import React, {
    useContext, useEffect, useMemo, useState 
} from 'react';
import {
    Box, Typography, useTheme 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, 
    TypographyWeights
} from '../../../../figmaTheme';

export const TargetSection = (): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const [skipAnimation, setSkipAnimation] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        },
        staticCheckmark: {
            width: '16.2px',
            height: '16.2px',
            margin: 'auto 0'
        },
        checkmark: {
            width: '80px',
            height: '80px',
            margin: '0 auto',
            transform: 'translate(0)',
            zIndex: 1,
            animation: `$moveCheckmark ${skipAnimation ? 0 : 0.5}s forwards 0s, $moveCheckmark2 ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s, $moveCheckmark3 ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s, $fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        sections: {
            height: '80px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            columnGap: '10px',
            marginTop: '50px',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.5}s`
        },
        section: {
            height: '80px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: '8px'
        },
        background: {
            margin: 'auto 0',
            backgroundColor: theme.palette.neutral['100'],
            borderRadius: '50%',
            width: '16px',
            height: '16px',
        },
        targetBackground: {
            margin: 'auto 0',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.neutral['100'],
            borderRadius: '50%',
            width: '16px',
            height: '16px',
            alignItems: 'center',
            justifyContent: 'center',
            animation: `$changeColor ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s, $semigrow ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s, $grow ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`,
        },
        icon: {
            fill: theme.palette.neutral['100'],
        },
        goalsSection: {
            opacity: 1,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        habitsSection: {
            opacity: 1,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        aboutYouSection: {
            opacity: 1,
            animation: `$fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        planSection: {
            opacity: 0,
        },
        targetSection: {
            opacity: 1,
            animation: `$moveSection ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`
        },
        targetIcon: {
            opacity: 0,
            fill: theme.palette.tertiary.main,
            width: '50%',
            height: '50%',
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s`,
        },
        title: {
            fontWeight: TypographyWeights.BOLD,
            textAlign: 'center',
            marginBottom: '8px',
            marginTop: '50px',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`
        },
        subtitle: {
            textAlign: 'center',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: '1' 
            },
            '100%': {
                opacity: '0' 
            },
        },
        '@keyframes changeColor': {
            '0%': {
                backgroundColor: theme.palette.neutral['100'] 
            },
            '100%': {
                backgroundColor: theme.palette.success.main
            },
        },
        '@keyframes semigrow': {
            '0%': {
                width: '16px',
                height: '16px' 
            },
            '100%': {
                width: '24px',
                height: '24px' 
            },
        },
        '@keyframes grow': {
            '0%': {
                width: '24px',
                height: '24px' 
            },
            '100%': {
                width: '80px',
                height: '80px' 
            },
        },
        '@keyframes moveSection': {
            '0%': {
                transform: 'translate(0)' 
            },
            '100%': {
                transform: 'translate(-52px, 0)' 
            },
        },
        '@keyframes moveCheckmark': {
            '0%': {
                transform: 'translate(0)',
                width: '80px',
                height: '80px' 
            },
            '100%': {
                transform: 'translate(25px, 98px)',
                width: '16.2px',
                height: '16.2px' 
            },
        },
        '@keyframes moveCheckmark2': {
            '0%': {
                transform: 'translate(25px, 98px)' 
            },
            '100%': {
                transform: 'translate(23px, 98px)' 
            },
        },
        '@keyframes moveCheckmark3': {
            '0%': {
                transform: 'translate(23px, 98px)' 
            },
            '100%': {
                transform: 'translate(-6px, 98px)' 
            },
        }
    })), [skipAnimation]);
    const classes = useStyles();
    const theme = useTheme();

    useEffect(() => {
        setTimeout(() => {
            setSkipAnimation(true);
        }, 2500);
    }, []);

    const onClick = () => {
        if (!skipAnimation) {
            setSkipAnimation(true);
        } else if (!isComplete) {
            formContext.completeDiagnostic();
            setIsComplete(true);
        }
    };

    const onBackClick = () => {
        formContext.goBack();
    };

    return (
        <CardContainer
            sectionKey={'checkout'}
            goBack={onBackClick}
            goNext={onClick}
            backgroundColor={theme.palette.white}
            hideHeaderText
        >
            <Card>
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.content}>
                        <svg
                            className={classes.checkmark} 
                            width="82" 
                            height="82" 
                            viewBox="0.5 0.5 82 82" 
                            fill="none" 
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle
                                cx="41"
                                cy="41"
                                r="40.5"
                                fill={theme.palette.neutral['400']}
                                stroke={theme.palette.neutral['400']}
                            />
                            <path
                                d="M20.1 47.2 l12.1 10.1 c0 0 1.7 1.7 2.9 -1.5 l25.4 -32.1"
                                stroke={theme.palette.white}
                                strokeWidth="7.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <Box className={classes.sections}>
                            <Box className={`${classes.section} ${classes.goalsSection}`}>
                                <svg
                                    className={classes.staticCheckmark}
                                    width="82"
                                    height="82"
                                    viewBox="0.5 0.5 82 82"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="41"
                                        cy="41"
                                        r="40.5"
                                        fill={theme.palette.neutral['400']}
                                        stroke={theme.palette.neutral['400']}
                                    />
                                    <path
                                        d="M20.1 47.2 l12.1 10.1 c0 0 1.7 1.7 2.9 -1.5 l25.4 -32.1"
                                        stroke={theme.palette.white}
                                        strokeWidth="7.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Box>
                            <Box className={`${classes.section} ${classes.habitsSection}`}>
                                <svg
                                    className={classes.staticCheckmark}
                                    width="82"
                                    height="82"
                                    viewBox="0.5 0.5 82 82"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="41"
                                        cy="41"
                                        r="40.5"
                                        fill={theme.palette.neutral['400']}
                                        stroke={theme.palette.neutral['400']}
                                    />
                                    <path
                                        d="M20.1 47.2 l12.1 10.1 c0 0 1.7 1.7 2.9 -1.5 l25.4 -32.1"
                                        stroke={theme.palette.white}
                                        strokeWidth="7.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Box>
                            <Box className={`${classes.section} ${classes.aboutYouSection}`}>
                                <svg
                                    className={classes.staticCheckmark}
                                    width="82"
                                    height="82"
                                    viewBox="0.5 0.5 82 82"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="41"
                                        cy="41"
                                        r="40.5"
                                        fill={theme.palette.neutral['400']}
                                        stroke={theme.palette.neutral['400']}
                                    />
                                    <path
                                        d="M20.1 47.2 l12.1 10.1 c0 0 1.7 1.7 2.9 -1.5 l25.4 -32.1"
                                        stroke={theme.palette.white}
                                        strokeWidth="7.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Box>
                            <Box className={`${classes.section} ${classes.planSection}`}>
                                <svg
                                    className={classes.staticCheckmark}
                                    width="82"
                                    height="82"
                                    viewBox="0.5 0.5 82 82"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="41"
                                        cy="41"
                                        r="40.5"
                                        fill={theme.palette.neutral['400']}
                                        stroke={theme.palette.neutral['400']}
                                    />
                                    <path
                                        d="M20.1 47.2 l12.1 10.1 c0 0 1.7 1.7 2.9 -1.5 l25.4 -32.1"
                                        stroke={theme.palette.white}
                                        strokeWidth="7.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Box>
                            <Box className={`${classes.section} ${classes.targetSection}`}>
                                <Box className={classes.targetBackground}>
                                    <svg
                                        className={`${classes.icon} ${classes.targetIcon}`}
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g clipPath="url(#clip0_3122_41290)">
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M5.32751 1.14468C5.60261 0.587188 6.39756 0.587192 6.67266 1.14468L7.96753 3.76879L10.8632 4.18953C11.4784 4.27891 11.7241 5.03488 11.2789 5.46879L9.18359 7.51132L9.67807 10.3953C9.78313 11.008 9.13998 11.4752 8.58977 11.1858L6.00008 9.82392L3.41039 11.1858C2.86019 11.4752 2.21704 11.008 2.32209 10.3953L2.81658 7.51132L0.721242 5.46879C0.276116 5.03488 0.521759 4.27891 1.13692 4.18953L4.03264 3.76879L5.32751 1.14468ZM6.00008 1.81554L4.80483 4.23777C4.69558 4.45915 4.4844 4.6126 4.24009 4.6481L1.5671 5.03647L3.50127 6.9219C3.67801 7.09419 3.75867 7.34242 3.71696 7.5857L3.26052 10.2478L5.65099 8.99064C5.86953 8.87572 6.13063 8.87572 6.34917 8.99064L8.73965 10.2478L8.28321 7.5857C8.24149 7.34242 8.32215 7.09419 8.4989 6.9219L10.4331 5.03647L7.76007 4.6481C7.51577 4.6126 7.30458 4.45916 7.19534 4.23777L6.00008 1.81554Z"
                                                fill={theme.palette.white}
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_3122_41290">
                                                <rect
                                                    width="12"
                                                    height="12"
                                                    fill={theme.palette.white}
                                                />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </Box>
                            </Box>
                        </Box>
                        <Typography
                            variant={TypographyVariants.H2}
                            className={classes.title}
                        >
                            {`${formContext.answers?.your_name}, let's do this!`}
                        </Typography>
                        <Typography
                            variant={TypographyVariants.H5}
                            className={classes.subtitle}
                        >
                            {'Next, you\'ll make a commitment to yourself by activating your free 15-day trial. If at any point you decide it\'s not for you, you can cancel anytime.'}
                        </Typography>
                    </Box>
                </Box>
            </Card>
        </CardContainer>
    );
};

export default TargetSection;
